import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, DollarSign, Package, Users, Calendar, Download } from 'lucide-react';
import api from '../api/client';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

function ReportesPage() {
  const [tipoReporte, setTipoReporte] = useState('ventas-periodo');
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0].slice(0, 8) + '01');
  const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0]);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);

  const tiposReporte = [
    { value: 'ventas-periodo', label: 'Ventas por Período', icon: Calendar },
    { value: 'rentabilidad-productos', label: 'Rentabilidad de Productos', icon: TrendingUp },
    { value: 'analisis-abc', label: 'Análisis ABC', icon: Package },
    { value: 'ventas-por-categoria', label: 'Ventas por Categoría', icon: Package },
    { value: 'ventas-por-vendedor', label: 'Ventas por Vendedor', icon: Users },
    { value: 'ticket-promedio', label: 'Ticket Promedio', icon: DollarSign },
    { value: 'proyeccion-ventas', label: 'Proyección de Ventas', icon: TrendingUp },
    { value: 'horas-pico', label: 'Horas Pico', icon: Calendar }
  ];

  const cargarReporte = async () => {
    setCargando(true);
    try {
      const params = new URLSearchParams({
        tipo: tipoReporte,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin
      });
      
      // Unificado en reportes.php para facilitar despliegue
      const response = await api.get(`/reportes.php?${params}`);
      setDatos(response.data.data);
    } catch (error) {
      console.error('Error al cargar reporte:', error);
      alert('Error al cargar el reporte. Asegúrate de haber actualizado api/reportes.php en el servidor.');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarReporte();
  }, [tipoReporte]);

  const exportarExcel = async () => {
    try {
      setCargando(true);
      const params = new URLSearchParams({
        tipo: 'ventas',
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin
      });
      
      const response = await api.get(`/reportes.php?${params}`);
      
      if (!response.data.success || !response.data.data || response.data.data.length === 0) {
        alert('No hay datos para exportar en este rango de fechas');
        return;
      }
      
      const ventas = response.data.data;
      
      let csvContent = "\uFEFF"; 
      csvContent += "Folio,Fecha,Cliente,Vendedor,Metodo Pago,Estado,Total\n";
      
      ventas.forEach(v => {
        const fecha = new Date(v.fecha_venta).toLocaleString('es-GT');
        const row = [
          v.folio,
          `"${fecha}"`,
          `"${(v.cliente_nombre || '').replace(/"/g, '""')}"`,
          `"${(v.vendedor_nombre || '').replace(/"/g, '""')}"`,
          v.metodo_pago,
          v.estado,
          v.total
        ].join(",");
        csvContent += row + "\n";
      });
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `reporte_ventas_${fechaInicio}_${fechaFin}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error al exportar:', error);
      alert('Error al generar la exportación');
    } finally {
      setCargando(false);
    }
  };

  const exportarPDF = async () => {
    try {
      setCargando(true);
      const params = new URLSearchParams({
        tipo: 'ventas',
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin
      });
      
      const response = await api.get(`/reportes.php?${params}`);
      
      if (!response.data.success || !response.data.data || response.data.data.length === 0) {
        alert('No hay datos para exportar');
        return;
      }
      
      const ventas = response.data.data;
      const totalPeriodo = ventas.reduce((sum, v) => sum + parseFloat(v.total), 0);
      
      const printWindow = window.open('', '_blank');
      
      const html = `
        <html>
        <head>
          <title>Reporte de Ventas</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #333; text-align: center; }
            .header { margin-bottom: 20px; text-align: center; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .total-row { font-weight: bold; background-color: #e6e6e6; }
            .text-right { text-align: right; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Reporte de Ventas</h1>
            <p>Desde: ${fechaInicio} Hasta: ${fechaFin}</p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Folio</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Vendedor</th>
                <th>Método</th>
                <th>Estado</th>
                <th class="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              ${ventas.map(v => `
                <tr>
                  <td>${v.folio}</td>
                  <td>${new Date(v.fecha_venta).toLocaleString('es-GT')}</td>
                  <td>${v.cliente_nombre}</td>
                  <td>${v.vendedor_nombre}</td>
                  <td>${v.metodo_pago}</td>
                  <td>${v.estado}</td>
                  <td class="text-right">Q${parseFloat(v.total).toFixed(2)}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="6" class="text-right">TOTAL PERIODO</td>
                <td class="text-right">Q${totalPeriodo.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
        </html>
      `;
      
      printWindow.document.write(html);
      printWindow.document.close();
      
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      alert('Error al generar PDF');
    } finally {
      setCargando(false);
    }
  };

  const renderReporte = () => {
    if (!datos) return null;

    switch(tipoReporte) {
      case 'ventas-periodo':
        return (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Ventas Diarias</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={datos}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fecha" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" stroke="#8884d8" name="Total" />
                  <Line type="monotone" dataKey="subtotal" stroke="#82ca9d" name="Subtotal" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Total Ventas</p>
                <p className="text-2xl font-bold text-blue-600">
                  Q{datos.reduce((sum, d) => sum + parseFloat(d.total), 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Promedio Diario</p>
                <p className="text-2xl font-bold text-green-600">
                  Q{(datos.reduce((sum, d) => sum + parseFloat(d.total), 0) / datos.length).toFixed(2)}
                </p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Total Transacciones</p>
                <p className="text-2xl font-bold text-purple-600">
                  {datos.reduce((sum, d) => sum + parseInt(d.num_ventas), 0)}
                </p>
              </div>
            </div>
          </div>
        );

      case 'rentabilidad-productos':
        return (
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Productos Más Rentables</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Producto</th>
                    <th className="px-4 py-2 text-right">Vendidos</th>
                    <th className="px-4 py-2 text-right">Ventas</th>
                    <th className="px-4 py-2 text-right">Costo</th>
                    <th className="px-4 py-2 text-right">Utilidad</th>
                    <th className="px-4 py-2 text-right">Margen %</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.slice(0, 20).map((producto, index) => (
                    <tr key={index} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">{producto.nombre}</td>
                      <td className="px-4 py-2 text-right">{producto.cantidad_vendida}</td>
                      <td className="px-4 py-2 text-right">Q{parseFloat(producto.ventas_netas).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right">Q{parseFloat(producto.costo_total).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-green-600">
                        Q{parseFloat(producto.utilidad).toFixed(2)}
                      </td>
                      <td className="px-4 py-2 text-right">{parseFloat(producto.margen_porcentaje).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'analisis-abc':
        const clasificacionA = datos.filter(p => p.clasificacion === 'A');
        const clasificacionB = datos.filter(p => p.clasificacion === 'B');
        const clasificacionC = datos.filter(p => p.clasificacion === 'C');
        
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 p-6 rounded-lg">
                <h4 className="font-semibold text-green-800">Clase A (Top 80%)</h4>
                <p className="text-3xl font-bold text-green-600 mt-2">{clasificacionA.length}</p>
                <p className="text-sm text-gray-600">productos</p>
              </div>
              <div className="bg-yellow-50 p-6 rounded-lg">
                <h4 className="font-semibold text-yellow-800">Clase B (15%)</h4>
                <p className="text-3xl font-bold text-yellow-600 mt-2">{clasificacionB.length}</p>
                <p className="text-sm text-gray-600">productos</p>
              </div>
              <div className="bg-red-50 p-6 rounded-lg">
                <h4 className="font-semibold text-red-800">Clase C (5%)</h4>
                <p className="text-3xl font-bold text-red-600 mt-2">{clasificacionC.length}</p>
                <p className="text-sm text-gray-600">productos</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Detalle de Clasificación</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Producto</th>
                      <th className="px-4 py-2 text-center">Clase</th>
                      <th className="px-4 py-2 text-right">Ventas</th>
                      <th className="px-4 py-2 text-right">% Total</th>
                      <th className="px-4 py-2 text-right">% Acumulado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.slice(0, 30).map((producto, index) => (
                      <tr key={index} className="border-t hover:bg-gray-50">
                        <td className="px-4 py-2">{producto.nombre}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-1 rounded text-white ${
                            producto.clasificacion === 'A' ? 'bg-green-500' :
                            producto.clasificacion === 'B' ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}>
                            {producto.clasificacion}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">Q{parseFloat(producto.ventas_total).toFixed(2)}</td>
                        <td className="px-4 py-2 text-right">{parseFloat(producto.porcentaje_ventas).toFixed(2)}%</td>
                        <td className="px-4 py-2 text-right">{parseFloat(producto.porcentaje_acumulado).toFixed(2)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'ventas-por-categoria':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Distribución por Categoría</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={datos}
                    dataKey="total_ventas"
                    nameKey="categoria"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {datos.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Detalle por Categoría</h3>
              <div className="space-y-3">
                {datos.map((cat, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div>
                      <p className="font-semibold">{cat.categoria}</p>
                      <p className="text-sm text-gray-600">{cat.productos_vendidos} productos</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-blue-600">Q{parseFloat(cat.total_ventas).toFixed(2)}</p>
                      <p className="text-sm text-gray-600">{cat.num_ventas} ventas</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'ticket-promedio':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-sm text-gray-600">Ticket Promedio</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">
                Q{parseFloat(datos.ticket_promedio).toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-sm text-gray-600">Total de Ventas</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{datos.num_ventas}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-sm text-gray-600">Venta Total</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">
                Q{parseFloat(datos.total_vendido).toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-sm text-gray-600">Venta Mínima</p>
              <p className="text-3xl font-bold text-orange-600 mt-2">
                Q{parseFloat(datos.venta_minima).toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-sm text-gray-600">Venta Máxima</p>
              <p className="text-3xl font-bold text-red-600 mt-2">
                Q{parseFloat(datos.venta_maxima).toFixed(2)}
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-sm text-gray-600">Productos por Venta</p>
              <p className="text-3xl font-bold text-indigo-600 mt-2">
                {parseFloat(datos.productos_por_venta).toFixed(1)}
              </p>
            </div>
          </div>
        );

      case 'horas-pico':
        return (
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Ventas por Hora del Día</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={datos}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hora" label={{ value: 'Hora', position: 'insideBottom', offset: -5 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="num_ventas" fill="#8884d8" name="Número de Ventas" />
                <Bar dataKey="total_ventas" fill="#82ca9d" name="Total Vendido" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        );

      default:
        return <div>Selecciona un tipo de reporte</div>;
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Reportes</h1>
        <div className="flex gap-2">
          <button
            onClick={exportarPDF}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            <Download size={20} />
            Exportar PDF
          </button>
          <button
            onClick={exportarExcel}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            <Download size={20} />
            Exportar Excel
          </button>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Reporte
            </label>
            <select
              value={tipoReporte}
              onChange={(e) => setTipoReporte(e.target.value)}
              className="w-full px-3 py-2 border rounded"
            >
              {tiposReporte.map(tipo => (
                <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha Fin
            </label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="w-full px-3 py-2 border rounded"
            />
          </div>
          
          <div className="flex items-end">
            <button
              onClick={cargarReporte}
              disabled={cargando}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {cargando ? 'Cargando...' : 'Generar'}
            </button>
          </div>
        </div>
      </div>

      {cargando ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-xl">Cargando reporte...</div>
        </div>
      ) : (
        renderReporte()
      )}
    </div>
  );
}

export default ReportesPage;
